/* eslint-disable @atlassian/relay/unused-fields */
/* eslint-disable @atlassian/relay/graphql-naming */
/* eslint-disable @atlassian/relay/no-wrapping-hooks */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
	graphql,
	usePreloadedQuery,
	useRefetchableFragment,
	usePaginationFragment,
	type PreloadedQuery,
	type Disposable,
} from 'react-relay';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';

import type {
	ObjectListFlyoutHookParams,
	ObjectListFlyoutHookResults,
} from '@atlassian/navigation-common/types';

import { useIntl } from '@atlassian/jira-intl';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import {
	FAVORITE_TYPE_FILTER_MAP,
	VALID_FAVORITE_TYPES,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-content-starred/src/common/constants.tsx';
import { getSections } from './StarredGetSections.tsx';
import type { JiraStarredOLFItem } from './JiraRecentOLFItem.types.tsx';
import type { useStarredItemsPaged$key } from './__generated__/useStarredItemsPaged.graphql.ts';
import type { useStarredItemsPaginationQuery } from './__generated__/useStarredItemsPaginationQuery.graphql.ts';
import type {
	useStarredItemsRefetchableQuery,
	JiraFavouriteFilter,
} from './__generated__/useStarredItemsRefetchableQuery.graphql.ts';
import type { useStarredItemsQuery } from './__generated__/useStarredItemsQuery.graphql.ts';
import type { useStarredItems$key } from './__generated__/useStarredItems.graphql.ts';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';

const useAccountIdWithFG = functionWithCondition(
	() => fg('jpd-enable-roadmaps-recents-fe'),
	useAccountId,
	() => null,
);

const DEBOUNCE_INTERVAL = 300;

type PreloadedQueries = {
	starredMenuData: PreloadedQuery<useStarredItemsQuery>;
};

export function useStarredItems(
	params: ObjectListFlyoutHookParams<PreloadedQueries>,
): ObjectListFlyoutHookResults<JiraStarredOLFItem> {
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const { searchText, selectedFilters, queries } = params as {
		searchText: string;
		selectedFilters: string[];
		queries: PreloadedQueries;
	};
	const { formatMessage } = useIntl();
	const userLocale = useLocale();
	const { starredMenuData } = queries;
	const { cloudId, count } = starredMenuData.variables;
	const selectedFilter =
		(Array.isArray(selectedFilters) ? selectedFilters[0] : FAVORITE_TYPE_FILTER_MAP.ALL) ||
		FAVORITE_TYPE_FILTER_MAP.ALL;

	const queryData = usePreloadedQuery<useStarredItemsQuery>(
		graphql`
			query useStarredItemsQuery($cloudId: ID!, $filter: JiraFavouriteFilter!, $count: Int!)
			@preloadable {
				jira @required(action: THROW) {
					allFavourites: favourites(cloudId: $cloudId, filter: $filter, first: 1) {
						pageInfo @required(action: THROW) {
							startCursor
						}
					}
				}
				...useStarredItems @arguments(count: $count)
			}
		`,
		starredMenuData,
	);

	const [fragmentData, refetch] = useRefetchableFragment<
		useStarredItemsRefetchableQuery,
		useStarredItems$key
	>(
		graphql`
			fragment useStarredItems on Query
			@argumentDefinitions(count: { type: "Int!" })
			@refetchable(queryName: "useStarredItemsRefetchableQuery") {
				...useStarredItemsPaged @arguments(count: $count)
			}
		`,
		queryData,
	);

	// NOTE: refetch() returns a Disposable resource. This resource should be dispose()'d of
	// before subsequent calls to refetch.
	// We also want to debounce changes-to-the-search-string.
	const disposableSearchRef = useRef<Disposable | null>(null);

	const handleFilterChange = useCallback(
		(newSearch: string, newFilter: string) => {
			// oxlint-disable-next-line typescript/consistent-type-assertions
			const filterQuery = {
				sort: { order: 'DESC' as const },
				...(newFilter === FAVORITE_TYPE_FILTER_MAP.ALL
					? { types: VALID_FAVORITE_TYPES }
					: { type: newFilter }),
			} as JiraFavouriteFilter;

			if (newSearch) {
				filterQuery.keyword = newSearch;
			}

			// Note: To avoid an extra network call, the filterQuery has to match the initial (prefetched)
			//  filterQuery EXACTLY. For example, the initial query had the `sort` prop, but when filterQuery
			//  didn't have this props, it triggered a refetch after the panel had rendered.
			//  refetch() is checking to see if the props change, to determine what it should do.
			// Note 2: We need to add the fetchPolicy so that the data refreshes correctly when using the star-button
			//  on a menu item. Without this fetchPolicy, the star-state can go out-of-sync with the menu item.
			return refetch({ cloudId, filter: filterQuery }, { fetchPolicy: 'store-and-network' });
		},
		[cloudId, refetch],
	);

	const [handleDebouncedSearchChange] = useDebouncedCallback<
		(newSearchQuery?: string, newFilter?: string) => void
	>((newSearchQuery = searchText, newFilter = selectedFilter) => {
		disposableSearchRef.current?.dispose();
		disposableSearchRef.current = handleFilterChange(newSearchQuery ?? '', newFilter);
	}, DEBOUNCE_INTERVAL);

	useEffect(() => {
		handleDebouncedSearchChange(searchText ?? '', selectedFilter);
	}, [handleDebouncedSearchChange, searchText, selectedFilter]);

	const { data, /* isLoadingNext, */ hasNext, loadNext } = usePaginationFragment<
		useStarredItemsPaginationQuery,
		useStarredItemsPaged$key
	>(
		graphql`
			fragment useStarredItemsPaged on Query
			@refetchable(queryName: "useStarredItemsPaginationQuery")
			@argumentDefinitions(
				cursor: { type: "String" }
				count: { type: "Int" }
				isJpdRoadmapsRecentsEnabled: {
					type: "Boolean!"
					provider: "@jira/platform__graphql-relay-provider/src/isJpdRoadmapsRecentsEnabled"
				}
			) {
				jira @required(action: THROW) {
					starredItems: favourites(
						cloudId: $cloudId
						filter: $filter
						first: $count
						after: $cursor
					) @connection(key: "useStarredItemsPaged_starredItems") {
						edges {
							node {
								__typename
								... on JiraProject {
									id
									lastViewedTimestamp
									# ...JiraRecentMenuProjectItem
									projectTitle: name
									key
									defaultNavigationItem {
										... on JiraNavigationItem {
											url
										}
									}
									description
									avatar {
										large
									}
									projectId
								}
								... on JiraPlan {
									id
									lastViewedTimestamp
									# ...JiraRecentMenuPlanItem
									planId
									scenario {
										scenarioId
									}
									planTitle: title
								}
								... on JiraBoard {
									id
									lastViewedTimestamp
									# ...JiraRecentMenuBoardItem
									boardTitle: name
									boardUrl
									boardId
								}
								... on JiraDashboard {
									id
									lastViewedTimestamp
									# ...JiraRecentMenuDashboardItem
									dashboardTitle: title
									dashboardUrl
									dashboardId
								}
								... on JiraSystemFilter {
									id
									lastViewedTimestamp
									# ...JiraRecentMenuFilterItem
									filterTitle: name
									filterUrl
									filterId
								}
								... on JiraCustomFilter {
									id
									lastViewedTimestamp
									# ...JiraRecentMenuFilterItem
									filterTitle: name
									filterUrl
									filterId
								}
								... on JiraServiceManagementQueue {
									id
									lastViewedTimestamp
									# ...JSMRecentMenuQueueItem
									queueTitle: title
									queueUrl
									queueId
								}
								... on JiraProductDiscoveryCollection @include(if: $isJpdRoadmapsRecentsEnabled) {
									id
									lastViewedTimestamp
									collectionTitle: title
									collectionUrl
									collectionId
									emoji
								}
							}
						}
						pageInfo {
							hasNextPage
							endCursor
						}
					}
				}
			}
		`,
		fragmentData,
	);
	const accountId = useAccountIdWithFG();
	const deps = useMemo(
		() => ({ formatMessage, locale: userLocale, cloudId, accountId }),
		[formatMessage, userLocale, cloudId, accountId],
	);

	const { sections } = useMemo(() => {
		const nodes = data.jira.starredItems?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];
		return {
			sections: getSections(nodes, deps),
		};
	}, [data.jira.starredItems?.edges, deps]);

	const loadMore = useCallback(() => loadNext(count), [loadNext, count]);

	if (searchText === '') {
		return { sections, hasMore: hasNext, isLoading: false, loadMore };
	}

	return {
		hasMore: hasNext,
		isLoading: false,
		loadMore,
		sections,
	};
}
